/**
 * Lab / bet-service workstation APIs → Detail Book UI model.
 *
 * - `getTotalBooks1` → runners + root tree (+ optional `liveRates` catalog seed)
 * - `getBetDetailsForWorkstation` → bet ticker rows
 * - `getMemberNetExposure` → headline NE
 * - `getBooksForBackend` (market-scoped) → event/market metadata fallback
 * - `getBetfairBook` / `getBetfairBookLineSession` → CM local vs Betfair strip
 */

import type { BookType } from '@/features/net-exposure/fixture'
import type {
  BookEventGroupBackend,
  BookHorseAmount,
  BookMarketBackend,
} from '@/api/map/books'
import type { LiveRatePayload } from '@/api/map/rates'
import type {
  WorkstationBet,
  WorkstationBetSide,
  WorkstationBook,
  WorkstationHorseAmount,
  WorkstationMarketType,
  WorkstationRunner,
  WorkstationTreeNode,
} from '@/features/net-exposure/workstation-fixture'
import { isLineSessionBetListMarket } from '@/features/net-exposure/workstation/lineSessionBetOdds'

/** OpenAPI `TotalBooks1Request`. */
export type TotalBooks1Request = {
  user: string
  marketId: string
  loggedInMember?: string
  username?: string
  accessLevel?: string
  selectedType?: BookType | string
  page?: number
}

/** OpenAPI `TotalBooks1Response` book row. */
export type TotalBooks1BookRow = BookMarketBackend & {
  username?: string
}

export type TotalBooks1Response = {
  page: number
  total: number
  type?: 'matchodds' | 'bookmaker' | 'fancy' | 'lineLadder'
  book: TotalBooks1BookRow[]
  /**
   * Socket-shaped catalog odds from Agent bet-service (`market.rates` / BM runners).
   * Seeds Detail Book before the first SSE tick (Frontend Player-Markets parity).
   */
  liveRates?: LiveRatePayload
}

/** Lab `Book/getLadder` (+ getLadderTotal) body. */
export type GetLadderRequest = {
  sessionId: string
  userId: string
  level?: string
  selectedType?: BookType | string
  type?: string
}

export type FancyPtLadderRow = {
  startScore: number
  endScore: number | 'Infinite'
  amount: number
  amountCommission: number
}

/** Lab `Bet.createLadder` data payload. */
export type GetLadderResponse = {
  ladder: FancyPtLadderRow[]
  isBetPlaced: boolean
  matchData?: Array<{ runs: number; lt: number; gt: number }>
}

/** OpenAPI `MemberNetExposureRequest` / response. */
export type MemberNetExposureRequest = {
  _id: string
  marketId?: string
}

export type MemberNetExposureResponse = {
  netExposure: number
}

/** OpenAPI `BetDetailsForWorkstationRequest`. */
export type BetDetailsForWorkstationRequest = {
  user: string
  marketId: string
  /** Lab member Details — scopes rows to this player when set. */
  playerId?: string
  page?: number
  maxRow?: number
  userName?: string | string[]
  currencyType?: string
  fromDate?: string
  toDate?: string
  accessLevel?: string
}

/** OpenAPI `WorkstationBetRow`. */
export type WorkstationBetRow = {
  betId: string
  userName: string
  selectionName: string
  marketName: string
  event: string
  type: string
  stake: number
  betRate: number
  /** Fancy / session line (orders.runs). Null/omitted for match odds. */
  runs?: number | null
  averagePriceMatched?: number
  amountExecuted?: number
  amountToBePlacedOnBetfair?: number
  status: string
  IP?: string
  currencyType?: string
  marketId: string
  placedDate?: string | null
  matchedTime?: string | null
  createdAt?: string | null
  player?: { rate?: number; id?: string }
}

export type BetDetailsForWorkstationResponse = {
  result: WorkstationBetRow[]
  count: number
  maxRow: number
}

/** OpenAPI `InvalidRealTimeBetRequest` / response. */
export type RevalidRealTimeBetRequest = {
  _id: string
  revalidBy?: string
  reason?: string
}

export type RevalidRealTimeBetResponse = {
  message: string
  betId: string
  status: string
}

export type InvalidRealTimeBetRequest = {
  _id: string | string[]
  reason: string
  invalidBy?: string
  invalidMemberIp?: string
  clientIP?: string
  token?: string
}

export type InvalidRealTimeBetResponse = {
  message: string
  betIds: string[]
  count: number
}

export function labTypeToWorkstation(
  type: TotalBooks1Response['type'] | undefined,
  marketName?: string,
  fallback: WorkstationMarketType = 'match',
): WorkstationMarketType {
  if (type === 'fancy' || type === 'lineLadder') return 'fancy'
  if (type === 'bookmaker') return 'bookmaker'
  if (type === 'matchodds') return 'match'
  const n = (marketName || '').toLowerCase()
  if (n.includes('fancy') || n.includes('session')) return 'fancy'
  if (n.includes('bookmaker') || (marketName || '').includes('#')) return 'bookmaker'
  return fallback
}

function emptyOdds(): { back: WorkstationRunner['back']; lay: WorkstationRunner['lay'] } {
  return {
    back: [
      { price: 0, size: 0 },
      { price: 0, size: 0 },
      { price: 0, size: 0 },
    ],
    lay: [
      { price: 0, size: 0 },
      { price: 0, size: 0 },
      { price: 0, size: 0 },
    ],
  }
}

function horseAmount(h: BookHorseAmount): number {
  return Number(h.amount) || 0
}

function horseName(h: BookHorseAmount, index: number): string {
  const named = (h.name ?? '').trim()
  if (named) return named
  const id = String(h.horse || '').trim()
  return id || `Runner ${index + 1}`
}

function isDrawHorseName(name: string): boolean {
  return /^(x|the draw|draw)$/i.test(name.trim())
}

/** Lab / list grid: Draw / X always last among runners. */
export function horsesDrawLast<T extends { name?: string }>(horses: T[]): T[] {
  if (horses.length < 3) return horses
  const drawAt = horses.findIndex((h) => isDrawHorseName(h.name ?? ''))
  if (drawAt < 0 || drawAt === horses.length - 1) return horses
  const draw = horses[drawAt]
  if (!draw) return horses
  return [...horses.filter((_, i) => i !== drawAt), draw]
}

/** Build runners from Lab horse P/L. Live batb/batl overlay comes from Socket-API. Draw column last. */
export function runnersFromHorses(
  horses: BookHorseAmount[] | undefined,
  opts?: { allowEmpty?: boolean },
): WorkstationRunner[] {
  const list = horsesDrawLast(horses ?? [])
  if (list.length === 0) {
    // Fancy/session Detail Book often has no horse until API fills No|Yes —
    // do not invent Match Odds "1"/"2" zeros (painted blank exposure).
    if (opts?.allowEmpty) return []
    return [
      { id: '1', name: '1', amountPt: 0, amountTotal: 0, ...emptyOdds() },
      { id: '2', name: '2', amountPt: 0, amountTotal: 0, ...emptyOdds() },
    ]
  }
  return list.map((h, i) => {
    const amount = horseAmount(h)
    const id = String(h.horse || i + 1)
    return {
      id,
      name: horseName(h, i),
      amountPt: amount,
      amountTotal: amount,
      ...emptyOdds(),
    }
  })
}

function treeHorseFromRunners(runners: WorkstationRunner[]): WorkstationHorseAmount[] {
  return runners.map((r) => ({
    runnerId: r.id,
    amountPt: r.amountPt,
    amountTotal: r.amountTotal,
  }))
}

/**
 * Single-node tree from getTotalBooks1 book[0] (Lab first paint before downline expand).
 *
 * Match Odds Net Exposure = worst case = min(horse amounts), never the sum.
 * Do **not** let `getMemberNetExposure` (player-level sum for the agent login,
 * often 0) clobber that — it painted Rs.0 while horses showed +10 / −4.
 */
export function treeFromTotalBook(
  row: TotalBooks1BookRow,
  runners: WorkstationRunner[],
  memberNe?: number | null,
  marketType?: WorkstationMarketType,
  accessLevel?: string,
): WorkstationTreeNode {
  const fancy =
    marketType === 'fancy' || Boolean(row.fancyFlag) || Boolean(row.lineLadder)
  const amounts = runners.map((r) => r.amountPt)
  let minNe: number
  if (fancy) {
    minNe =
      row.netExposure != null && Number.isFinite(Number(row.netExposure))
        ? Number(row.netExposure)
        : row.totalNetExposure != null && Number.isFinite(Number(row.totalNetExposure))
          ? Number(row.totalNetExposure)
          : amounts.length > 0
            ? Math.min(...amounts)
            : memberNe != null && Number.isFinite(memberNe)
              ? memberNe
              : 0
  } else if (amounts.length > 0) {
    minNe = Math.min(...amounts)
  } else if (row.totalNetExposure != null && Number.isFinite(Number(row.totalNetExposure))) {
    minNe = Number(row.totalNetExposure)
  } else if (memberNe != null && Number.isFinite(memberNe)) {
    minNe = memberNe
  } else {
    minNe = Number(row.netExposure) || 0
  }
  const fancyNe = fancy ? minNe : Number(row.netExposure) || minNe
  return {
    id: row.user || 'self',
    username: row.username?.trim() || 'self',
    level: 'agent',
    ...(accessLevel ? { accessLevel } : {}),
    minNetExposurePt: minNe,
    minNetExposureTotal: minNe,
    netExposurePt: fancyNe,
    netExposureTotal: fancyNe,
    horse: treeHorseFromRunners(runners),
  }
}

/**
 * Lab `net-exposure-tracker` `getParticularBooks` child row → tree node.
 * Horse amounts come from the child book; columns stay the parent market runners.
 */
export function treeNodeFromDownlineBook(
  row: TotalBooks1BookRow,
  runners: WorkstationRunner[],
  marketType: WorkstationMarketType,
  meta: { username?: string; level?: 'agent' | 'player'; accessLevel?: string } = {},
): WorkstationTreeNode {
  const byHorse = new Map(
    (row.horse ?? []).map((h) => [String(h.horse), Number(h.amount) || 0]),
  )
  const childRunners = runners.map((r) => {
    const amount = byHorse.has(r.id) ? byHorse.get(r.id)! : 0
    return { ...r, amountPt: amount, amountTotal: amount }
  })
  const base = treeFromTotalBook(row, childRunners, null, marketType, meta.accessLevel)
  return {
    ...base,
    id: String(row.user || base.id),
    username: (meta.username || row.username || base.username).trim() || base.username,
    level: meta.level ?? 'agent',
    ...(meta.accessLevel ? { accessLevel: meta.accessLevel } : {}),
  }
}

/** Lab keeps children with non-empty horse books only. */
export function hasDownlineBookPosition(row: TotalBooks1BookRow | undefined | null): boolean {
  return Boolean(row?.horse && row.horse.length > 0)
}

/** Lab bet ticker → workstation bet row (matched/unmatched like workstation.js). */
export function toWorkstationBet(row: WorkstationBetRow): WorkstationBet {
  // POINTS: stake already INR majors; ignore player.rate (shareBp ≠ rate).
  // LS bet-list Matched/PL: Lab INR majors; other markets still ÷100 Pts in UI.
  const ccy = String(row.currencyType || 'INR').toUpperCase()
  const points = ccy === 'POINTS' || ccy === 'POINT@1'
  const rate = points
    ? 1
    : row.player?.rate != null && Number.isFinite(row.player.rate)
      ? Number(row.player.rate)
      : 1
  const stake = (Number(row.stake) || 0) * rate
  const amountExecuted = Number(row.amountExecuted) || 0
  const amountToPlace = Number(row.amountToBePlacedOnBetfair) || stake || 1
  const pct = amountToPlace > 0 ? (amountExecuted * 100) / amountToPlace : 100
  const matchedAmt = (stake * pct) / 100
  const unMatchedAmt = Math.max(0, stake - matchedAmt)
  const betRate = Number(row.betRate) || 0
  const averagePriceMatched =
    row.averagePriceMatched && Number(row.averagePriceMatched) !== 0
      ? Number(row.averagePriceMatched)
      : betRate
  const sideRaw = String(row.type || 'BACK').toUpperCase()
  const type: WorkstationBetSide = sideRaw === 'LAY' ? 'LAY' : 'BACK'
  // Lab lineLadder: (betRate − 1) × matched — betRate is 2, not runs
  // (averagePriceMatched / Odds Matched column).
  const plOdds = isLineSessionBetListMarket(row.marketName)
    ? betRate > 0
      ? betRate
      : 2
    : averagePriceMatched
  const profitLiability =
    type === 'BACK' ? Math.max(0, matchedAmt * (plOdds - 1)) : matchedAmt

  return {
    id: row.betId,
    userName: row.userName || '',
    currencyType: row.currencyType || 'INR',
    placedAt: row.placedDate || row.createdAt || new Date().toISOString(),
    matchedAt: row.matchedTime ?? null,
    status: row.status || '',
    event: row.event || '',
    marketName: row.marketName || '',
    selectionName: row.selectionName || '',
    type,
    betRate,
    runs: row.runs != null && Number.isFinite(Number(row.runs)) ? Number(row.runs) : null,
    averagePriceMatched,
    matchedAmt,
    unMatchedAmt,
    profitLiability,
    ip: row.IP || '',
  }
}

export function marketFromBooksGroups(
  groups: BookEventGroupBackend[] | undefined,
  marketId: string,
): BookMarketBackend | undefined {
  for (const g of groups ?? []) {
    const hit = g.data.find((m) => m.marketId === marketId)
    if (hit) return hit
  }
  return undefined
}

export type AssembleWorkstationInput = {
  marketId: string
  bookType: BookType
  total?: TotalBooks1Response | null
  booksMarket?: BookMarketBackend | null
  bets?: BetDetailsForWorkstationResponse | null
  memberNe?: number | null
  /** Route/search fallbacks when API omits names. */
  fallback?: {
    eventName?: string
    marketName?: string
    type?: WorkstationMarketType
    /** Sport / eventType when books row omits it (HR/GHR Lay hide). */
    eventType?: string
    username?: string
    /** Lab book.level for root PT ladder (e.g. Master). */
    accessLevel?: string
  }
}

/** OpenAPI `BetfairBookResponse` (match odds / bookmaker). */
export type BetfairProfitAndLoss = {
  selectionId: string | number
  ifWin?: number
  ifLose?: number
  percentMatched?: number
}

export type BetfairMarketPnL = {
  marketId: string
  profitAndLosses: BetfairProfitAndLoss[]
}

export type LocalBetfairHorse = {
  horse: string
  amount: number
  name?: string
}

export type LocalBetfairBook = {
  marketId: string
  horse: LocalBetfairHorse[]
  netExposure: number
}

export type BetfairBookResponse = {
  bookFromBetfair: BetfairMarketPnL[]
  bookFromLocal: LocalBetfairBook | null
}

/** OpenAPI line-session local book (subset used by UI). */
export type LocalLineSessionBook = {
  marketId: string
  selectionId?: string | null
  netExposure: number
  betfairBook?: {
    book?: Record<string, number>
  }
}

export type BetfairBookLineSessionResponse = {
  bookFromBetfair: BetfairMarketPnL[]
  bookFromLocal: LocalLineSessionBook | null
}

/**
 * Betfair line session only (`lineLadder` / overs–runs line) — picks
 * `getBetfairBookLineSession`. Does **not** treat custom fancy as line.
 * Uses Lab raw type / `lineLadder` flag, marketId `#`+`-`, and name heuristics
 * (UI maps lineLadder → fancy layout, so type alone is not enough).
 */
export function isLineSessionMarket(input: {
  marketType?: string | null
  marketId?: string | null
  marketName?: string | null
  lineLadder?: boolean | null
}): boolean {
  if (input.lineLadder === true) return true
  const marketType = String(input.marketType || '')
    .trim()
    .toLowerCase()
  if (marketType === 'lineladder' || marketType === 'line_ladder') return true
  // Never promote Match Odds / Bookmaker via id/name heuristics.
  if (
    marketType === 'match' ||
    marketType === 'matchodds' ||
    marketType === 'bookmaker'
  ) {
    return false
  }
  const marketId = String(input.marketId || '')
  const idLower = marketId.toLowerCase()
  if (
    marketId.includes('#') &&
    marketId.includes('-') &&
    !idLower.includes('bookmaker')
  ) {
    return true
  }
  const name = String(input.marketName || '')
    .trim()
    .toLowerCase()
  if (!name || name.includes('fancy') || name.includes('bookmaker')) return false
  return (
    name.includes('line session') ||
    name.includes('runs line') ||
    name.includes('overs line') ||
    name.includes('ball line') ||
    /\bline$/.test(name)
  )
}

/** Normalized CM comparison rows for BookTree (Lab work-station.html). */
export type BetfairCompareRow = {
  runnerId: string
  name: string
  localAmount: number
  betfairAmount: number
  percentMatched?: number
}

export type BetfairCompareView = {
  kind: 'match' | 'fancy'
  netExposureLocal: number
  netExposureBetfair: number | null
  rows: BetfairCompareRow[]
}

function plAmount(pl: BetfairProfitAndLoss | undefined, fancy: boolean): number {
  if (!pl) return 0
  return Number(fancy ? pl.ifLose : pl.ifWin) || 0
}

/**
 * Lab Winner7 always paints Betfair Book - betfair / local for Match Odds.
 * When exchange + local strips are empty, both rows show **0**.
 */
export function emptyBetfairCompareMatch(
  runners: { id: string; name: string }[],
): BetfairCompareView {
  return {
    kind: 'match',
    netExposureLocal: 0,
    netExposureBetfair: 0,
    rows: runners.map((r) => ({
      runnerId: r.id,
      name: r.name,
      localAmount: 0,
      betfairAmount: 0,
    })),
  }
}

/** Match/BM: Lab `betfairBook.horse` + `bookFromBetfair[0].profitAndLosses`. */
export function betfairCompareFromMatch(
  local: LocalBetfairBook | null | undefined,
  betfair: BetfairMarketPnL[] | undefined,
  runners: { id: string; name: string }[],
): BetfairCompareView | null {
  const pls = betfair?.[0]?.profitAndLosses ?? []
  const horses = local?.horse ?? []
  if (!local && !pls.length) {
    return runners.length > 0 ? emptyBetfairCompareMatch(runners) : null
  }
  const ids =
    runners.length > 0
      ? runners.map((r) => r.id)
      : [
          ...new Set([
            ...horses.map((h) => String(h.horse)),
            ...pls.map((p) => String(p.selectionId)),
          ]),
        ]

  const rows: BetfairCompareRow[] = ids.map((id) => {
    const localH = horses.find((h) => String(h.horse) === id)
    const pl = pls.find((p) => String(p.selectionId) === id)
    const runner = runners.find((r) => r.id === id)
    return {
      runnerId: id,
      name: runner?.name || localH?.name || id,
      localAmount: Number(localH?.amount) || 0,
      betfairAmount: plAmount(pl, false),
      percentMatched: pl?.percentMatched,
    }
  })

  const bfAmounts = rows.map((r) => r.betfairAmount)
  return {
    kind: 'match',
    netExposureLocal: Number(local?.netExposure) || 0,
    netExposureBetfair: bfAmounts.length ? Math.min(...bfAmounts) : 0,
    rows,
  }
}

/** Fancy/line: Lab uses ifLose from Betfair + local book map / netExposure. */
export function betfairCompareFromFancy(
  local: LocalLineSessionBook | null | undefined,
  betfair: BetfairMarketPnL[] | undefined,
  marketName: string,
): BetfairCompareView | null {
  if (!local && !(betfair?.[0]?.profitAndLosses?.length)) return null
  const pl = betfair?.[0]?.profitAndLosses?.[0]
  const selectionId =
    local?.selectionId != null
      ? String(local.selectionId)
      : pl
        ? String(pl.selectionId)
        : 'line'
  const bookMap = local?.betfairBook?.book
  let localAmount = Number(local?.netExposure) || 0
  if (bookMap && selectionId in bookMap) {
    localAmount = Number(bookMap[selectionId]) || 0
  } else if (bookMap) {
    const first = Object.values(bookMap)[0]
    if (first != null) localAmount = Number(first) || 0
  }

  return {
    kind: 'fancy',
    netExposureLocal: Number(local?.netExposure) || localAmount,
    netExposureBetfair: pl ? plAmount(pl, true) : null,
    rows: [
      {
        runnerId: selectionId,
        name: marketName || selectionId,
        localAmount,
        betfairAmount: plAmount(pl, true),
        percentMatched: pl?.percentMatched,
      },
    ],
  }
}

/**
 * Assemble a `WorkstationBook` from the four Lab workstation endpoints.
 * Returns null when there is no market book row to render.
 */
export function assembleWorkstationBook(input: AssembleWorkstationInput): WorkstationBook | null {
  const row =
    input.total?.book?.[0] ??
    (input.booksMarket
      ? ({ ...input.booksMarket, username: input.fallback?.username } as TotalBooks1BookRow)
      : undefined)
  if (!row) return null

  const type = labTypeToWorkstation(
    input.total?.type,
    row.marketName || input.fallback?.marketName,
    input.fallback?.type ?? 'match',
  )
  const lineLadder =
    input.total?.type === 'lineLadder' || Boolean(row.lineLadder)
  const runners = runnersFromHorses(row.horse, { allowEmpty: type === 'fancy' })
  const withUser: TotalBooks1BookRow = {
    ...row,
    username: row.username?.trim() || input.fallback?.username || row.username,
  }

  const eventType =
    String(row.eventType || input.fallback?.eventType || '').trim() || undefined

  return {
    marketId: input.marketId,
    type,
    marketName: row.marketName || input.fallback?.marketName || input.marketId,
    eventName: row.eventName || input.fallback?.eventName || 'Market',
    ...(eventType ? { eventType } : {}),
    status: 'OPEN',
    ...(lineLadder ? { lineLadder: true } : {}),
    runners,
    tree: treeFromTotalBook(
      withUser,
      runners,
      input.memberNe,
      type,
      input.fallback?.accessLevel,
    ),
    bets: (input.bets?.result ?? []).map(toWorkstationBet),
  }
}
